import React, { useCallback, useState } from 'react'
import { isDesktop, isMobile, isTablet } from 'react-device-detect'
import { When } from 'react-if'
import LazyHydrate from 'react-lazy-hydration'
import { LinkButton } from '@/atoms/Button'
import { GuildIconMono } from '@/atoms/Icons/GuildIconMono'
import { Image } from '@/atoms/Image'
import { CaptionSM, LabelMD, ParagraphMD, TitleLG, TitleXXS } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { Avatar } from '@/molecules/Avatar'
import { Asset, useAngelPlayerContext } from '@/organisms/AngelPlayer'
import { BitmovinPlayer } from '@/organisms/AngelPlayer/BitmovinPlayer'
import { CatalogTitle, formatTitleLikeLandscapeImagePath, formatTitleMoviePosterPath } from '@/services/ContentCatalog'
import { claimSharedContent, SharedContent } from '@/services/ContentShareService'
import { useGuildMember } from '@/services/GuildUserService'
import { Episode, Project } from '@/types/codegen-federation'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeTrack } from '@/utils/analytics'
import { getAnalyticsSessionId } from '@/utils/analytics/session-utils'
import { buildSignupUrl } from '@/utils/auth-utils'
import { writeToLocalStorage } from '@/utils/local-storage'
import { logger } from '@/utils/logging'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { Translate, TranslateFunction, useTranslate } from '@/utils/translate/translate-client'
import { UserPublicProfile } from '@/utils/users/usersServer'
import { GuildContentRecipientModal, GuildContentRecipientSheet, GuildJoinCTA } from './ContentRecipientComponents'

interface ContentSharedViewProps {
  sharedContent: SharedContent
  sharerPublicProfile: UserPublicProfile
  catalogTitle: CatalogTitle
  episode: { episode: Episode; project: Project }
  hasUser?: boolean
  project: string
  asset: Asset
  userUuid: string | null
  host?: string
}

export const ContentSharedView: React.FC<ContentSharedViewProps> = ({
  sharedContent,
  sharerPublicProfile,
  catalogTitle,
  episode,
  hasUser = false,
  project,
  userUuid,
}) => {
  const track = useSafeTrack()
  const { t } = useTranslate('watch')
  const { locale } = useLocale()
  const { isGuildMember } = useGuildMember()
  const sessionId = getAnalyticsSessionId()
  const { asset } = useAngelPlayerContext()
  const [showModal, setShowModal] = useState(true)
  const { asPath } = useSafeRouter()
  const contentClaimed = Boolean(
    sharedContent?.watched_at && sharedContent?.recipient_uuid && sharedContent.recipient_uuid !== userUuid,
  )
  const moviePosterPath = catalogTitle
    ? formatTitleMoviePosterPath(catalogTitle)
    : episode?.episode?.posterCloudinaryPath
    ? episode.episode.posterCloudinaryPath
    : null
  const movieMobilePosterPath = catalogTitle
    ? formatTitleLikeLandscapeImagePath(catalogTitle)
    : episode?.episode?.posterLandscapeCloudinaryPath
    ? episode.episode.posterLandscapeCloudinaryPath
    : null
  const loginUrl = buildSignupUrl(asPath)
  const sharerLocation = returnSharerLocation(sharerPublicProfile, t)
  const fallbackTitle = t('sharedFallbackTitle', '{{ firstName }} {{ lastName }} shared {{ project }} with you.', {
    project,
    firstName: sharerPublicProfile?.firstName,
    lastName: sharerPublicProfile?.lastName,
  })

  const handleModalClose = useCallback(() => {
    track('Guild Content Recipient Modal Closed', {
      sharer_uuid: sharedContent?.sharer_id,
      recipient_uuid: userUuid,
      episode_guid: episode?.episode?.id,
    })
    if (!contentClaimed && hasUser && userUuid) {
      setShowModal(false)
      if (userUuid !== sharedContent?.sharer_id) {
        claimSharedContent(userUuid, sharedContent?.id)
          .then(() => {
            writeToLocalStorage('sharer_uuid', sharedContent?.sharer_id)
          })
          .catch((error) => logger().error(`Failed to claim the shared guild content`, error))
      } else {
        logger().info(`User already claimed the shared guild content`, { userUuid })
      }
    } else null
  }, [track, sharedContent?.sharer_id, sharedContent?.id, userUuid, episode?.episode?.id, contentClaimed, hasUser])

  const handleLinkClick = useCallback(() => {
    track('Clicked Guild Content Shared CTA Button', {
      sharer_uuid: sharedContent?.sharer_id,
      recipient_uuid: userUuid,
      episode_guid: episode?.episode?.id,
    })
  }, [track, sharedContent?.sharer_id, userUuid, episode?.episode?.id])

  return (
    <main>
      <section className="relative w-full overflow-x-hidden bg-core-gray-950 pt-16 text-white">
        <When condition={Boolean(sharedContent && !contentClaimed && hasUser)}>
          <LazyHydrate whenVisible>
            <div className="pt-14">
              <BitmovinPlayer
                key={asset?.id}
                autoplay={false}
                loggedInUserId={userUuid}
                enableAds={false}
                asset={asset}
                uiLanguage={locale}
                sessionId={sessionId}
              />
            </div>
          </LazyHydrate>
        </When>
        <When condition={(sharedContent && contentClaimed) || !hasUser}>
          <div className={'aspect-h-9 aspect-w-16 mt-14 h-full w-full opacity-60'}>
            <Image
              fill={true}
              className="h-full w-full"
              src={episode.episode.posterLandscapeCloudinaryPath ?? movieMobilePosterPath ?? ''}
              alt="Wingfeather Saga frame"
            />
          </div>
        </When>
      </section>
      <LazyHydrate whenVisible>
        <section className="flex w-full flex-col-reverse justify-between gap-8 px-8 py-12 text-white lg:flex-row lg:gap-0 lg:px-16">
          <div className="flex max-w-[665px] flex-col gap-6">
            <TitleLG weight="bold">{project}</TitleLG>
            <ParagraphMD>{episode?.episode?.description ?? catalogTitle?.description?.long}</ParagraphMD>
          </div>
          <div className="flex flex-col gap-6">
            <div
              className="rounded-3xl p-[1px]"
              style={{
                background: 'linear-gradient(to bottom right, #EDD7C6, #D6A177, #FEDEC6, #D48D59, #A66141, #612B12)',
              }}
            >
              <div className="flex flex-row items-center gap-2 rounded-3xl bg-core-gray-800 p-4 lg:gap-8">
                <div className="flex lg:hidden">
                  <Avatar src={sharerPublicProfile?.image} size={46} />
                </div>
                <div className="hidden lg:flex">
                  <Avatar src={sharerPublicProfile?.image} size={74} />
                </div>
                <div className="flex max-w-[300px] flex-col gap-2">
                  <TitleXXS weight="bold" className="lg:photon-title-xs">
                    {sharerPublicProfile?.firstName} {sharerPublicProfile?.lastName}
                    {', '} {sharerLocation ? `${sharerLocation}, ` : ''}
                    <Translate i18nKey="sharedTitleWithYou" t={t} values={{ project }}>
                      shared <i>{{ project }}</i> with you!
                    </Translate>
                  </TitleXXS>
                  <When condition={!isGuildMember}>
                    <CaptionSM weight="medium" className="lg:photon-caption-md">
                      {t(
                        'guildMembersGetEarlyAccess',
                        `Angel Guild members get early access to Angel's newest shows and films.`,
                      )}
                    </CaptionSM>
                    <LinkButton
                      onClick={handleLinkClick}
                      variant="white"
                      target="_blank"
                      href={`${paths.guild.join}/${episode.project.slug}`}
                      className="mt-2 w-fit text-core-gray-950"
                    >
                      <div className="flex min-h-6 flex-row items-center">
                        <GuildIconMono color="black" size={20} />
                        <LabelMD className="pl-1">{t('becomeMember', 'Become a Member')}</LabelMD>
                      </div>
                    </LinkButton>
                  </When>
                </div>
              </div>
            </div>
          </div>
        </section>
      </LazyHydrate>
      <When condition={isTablet || isDesktop}>
        <GuildContentRecipientModal
          showModal={showModal}
          handleModalClose={handleModalClose}
          moviePosterPath={moviePosterPath}
          catalogTitle={catalogTitle}
          sharedContent={sharedContent}
          sharerPublicProfile={sharerPublicProfile}
          contentClaimed={contentClaimed}
          hasUser={hasUser}
          loginUrl={loginUrl}
          sharerLocation={sharerLocation}
          projectSlug={episode.project.slug ?? ''}
          fallbackTitle={fallbackTitle}
        />
      </When>
      <When condition={isMobile}>
        <GuildContentRecipientSheet
          showModal={showModal}
          handleModalClose={handleModalClose}
          moviePosterPath={movieMobilePosterPath}
          catalogTitle={catalogTitle}
          sharedContent={sharedContent}
          sharerPublicProfile={sharerPublicProfile}
          contentClaimed={contentClaimed}
          hasUser={hasUser}
          loginUrl={loginUrl}
          sharerLocation={sharerLocation}
          projectSlug={episode.project.slug ?? ''}
          fallbackTitle={fallbackTitle}
        />
      </When>
      <GuildJoinCTA episode={episode?.episode} project={episode?.project} moviePosterPath={moviePosterPath} />
    </main>
  )
}

const returnSharerLocation = (sharerPublicProfile: UserPublicProfile, t: TranslateFunction) => {
  return sharerPublicProfile?.city &&
    sharerPublicProfile.city.length > 0 &&
    sharerPublicProfile?.state &&
    sharerPublicProfile.state.length > 0
    ? `${t('from', 'From')} ${sharerPublicProfile.city}, ${sharerPublicProfile.state}`
    : sharerPublicProfile?.country && sharerPublicProfile.country.length > 0
    ? `${t('from', 'From')} ${sharerPublicProfile.country}`
    : ''
}
